// ─────────────────────────────────────────────────────────────────────────────
// 🔑 EDIT APPLICATION (Jesse, 2026-10-16): every change the borrower made to their submitted application —
// field, old, new, who, when (GET /api/admin/borrowers/:id/application-edits). Monitoring answers show only
// "(answered)"; an SSN/ITIN only its last four. A change after the credit pull (or to an identity field after
// verification) opens a review that staff close with what they checked (audited). Nothing was re-pulled.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const when = (t) => { try { return new Date(t).toLocaleString(); } catch (_) { return String(t || ''); } };

export default function ApplicationEditsCard({ borrowerId, token }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/application-edits`, { headers })
      .then((r) => setData(r.data || { edits: [], reviews: [] }))
      .catch(() => setData({ edits: [], reviews: [], unavailable: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId, token]);
  useEffect(() => { load(); }, [load]);
  if (!data || (!data.edits.length && !data.reviews.length)) return null;
  const openReview = data.reviews.find((r) => !r.resolved_at);
  const resolve = async () => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/application-edits/review`, { note }, { headers });
      setNote(''); setMsg('Marked reviewed.'); load();
    } catch (e) { setMsg(e?.response?.data?.message || 'Could not mark reviewed.'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-application-edits" className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="text-sm font-semibold text-gray-800 mb-1">Application edits</div>
      <div className="text-xs text-gray-500 mb-2">Changes the borrower made after submitting. Affected checks re-ran; credit was not pulled again.</div>
      {openReview && (
        <div data-testid="admin-application-edits-review" className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-3 text-xs text-amber-900">
          <div className="font-semibold mb-1">
            Review needed — edited {openReview.after_pull ? 'after the credit pull' : 'after identity verification'}: {(openReview.fields || []).join(', ')}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What you checked"
            className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white" data-testid="admin-application-edits-note" />
          <button type="button" disabled={busy || note.trim().length < 5} onClick={resolve} data-testid="admin-application-edits-resolve"
            className="mt-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#3a6ad9] disabled:bg-gray-300">Mark reviewed</button>
        </div>
      )}
      {msg && <div className="text-xs text-gray-600 mb-2">{msg}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-gray-500"><th className="py-1 pr-2">When</th><th className="pr-2">Field</th><th className="pr-2">Was</th><th className="pr-2">Now</th><th>By</th></tr></thead>
          <tbody>
            {data.edits.map((e) => (
              <tr key={e.id} className="border-t border-gray-100 align-top">
                <td className="py-1 pr-2 whitespace-nowrap">{when(e.created_at)}{e.after_pull ? ' · after pull' : ''}</td>
                <td className="pr-2 font-mono">{e.field}</td>
                <td className="pr-2 break-all">{e.old_value ?? '—'}</td>
                <td className="pr-2 break-all">{e.new_value ?? '—'}</td>
                <td>{e.actor_email || `#${e.actor_id}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
